/**
 * Robust Edge Function client with timeout, retry, and consistent error handling
 *
 * This module ensures:
 * - All edge calls have timeout (default 30s; search-jobs-backend 28s → use 30s for dashboard so 408 is received)
 * - 408 (timeout) and network/server errors retry with exponential backoff (max 2 retries)
 * - Consistent error classification (408 → timeout, retryable)
 * - Standardized response format and correlation IDs
 */

import { supabase, SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "@/integrations/supabase/client";
import { startSpan } from "@/lib/sentry";
import { logWarning, logError } from "@/lib/logger";
import { logRequest } from "@/lib/requestTracker";

export interface EdgeFunctionResponse<T = unknown> {
  ok: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    correlationId?: string;
    retryable?: boolean;
    /** DEV: status, code och raw meddelande för felsökning */
    debug?: string;
  };
  correlationId?: string;
}

export type ErrorType = "network" | "timeout" | "auth" | "server" | "client" | "rate_limit" | "unknown";

export interface EdgeFunctionError {
  type: ErrorType;
  message: string;
  correlationId?: string;
  retryable: boolean;
  originalError?: unknown;
}

/** Options for invoking an edge function. Body must be a JSON-serializable object. */
export interface EdgeInvokeOptions {
  body?: Record<string, unknown>;
  headers?: Record<string, string>;
  timeout?: number;
  maxRetries?: number;
  retryDelay?: number;
  requestId?: string;
}

/** Standard shape returned by edge functions: { ok?, success?, data?, error?, correlationId? }. */
export interface EdgeResponseShape<T = unknown> {
  success?: boolean;
  ok?: boolean;
  data?: T;
  correlationId?: string;
  error?: { code?: string; message?: string; correlationId?: string; retryable?: boolean };
  debugVersion?: string;
}

type CircuitState = {
  failures: number;
  openedAt?: number;
  lastFailureAt?: number;
};

type SupabasePublicConfig = {
  url: string;
  key: string;
};

const CIRCUIT_FUNCTIONS = new Set([
  "search-jobs-backend",
  "calculate-matches",
  "quick-search",
  "anonymous-match",
]);
const FALLBACK_FUNCTIONS = new Set(CIRCUIT_FUNCTIONS);
/** Never return or store fallback cache – show real data or clear error only (no "förenklad sökning" / stale jobs). */
const NO_FALLBACK_CACHE_FUNCTIONS = new Set(["search-jobs-backend"]);
const CIRCUIT_FAILURE_THRESHOLD = 3;
const CIRCUIT_WINDOW_MS = 60_000;
const CIRCUIT_COOLDOWN_MS = 30_000;
const FALLBACK_CACHE_TTL_MS = 5 * 60 * 1000;

const circuitStates = new Map<string, CircuitState>();
const fallbackCache = new Map<string, { data: unknown; storedAt: number }>();
const degradedEvents = new Map<string, number>();
const DIRECT_FETCH_FUNCTIONS = new Set(["search-jobs-backend", "dashboard-initial", "matches-initial", "applications-initial"]);

/** Use same resolved URL/key as Supabase client (includes production-domain override for findigo.se). Never null in browser. */
function getSupabasePublicConfig(): SupabasePublicConfig | null {
  if (typeof window === "undefined") return null;
  if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) return null;
  return { url: SUPABASE_URL, key: SUPABASE_PUBLISHABLE_KEY };
}

/** True when running on production (findigo.se). Use direct fetch first for critical functions to avoid SDK/CORS quirks. */
function isProductionOrigin(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "findigo.se" || host === "www.findigo.se";
}

function isCorsLikelyError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  const lowerMessage = message.toLowerCase();
  return (
    lowerMessage.includes("cors") ||
    lowerMessage.includes("preflight") ||
    lowerMessage.includes("blocked") ||
    lowerMessage.includes("failed to fetch") ||
    lowerMessage.includes("failed to send a request to the edge function")
  );
}

function getCircuitState(functionName: string): CircuitState {
  const existing = circuitStates.get(functionName);
  if (existing) return existing;
  const initial = { failures: 0 };
  circuitStates.set(functionName, initial);
  return initial;
}

function isCircuitEnabled(functionName: string): boolean {
  return CIRCUIT_FUNCTIONS.has(functionName);
}

function isCircuitOpen(functionName: string): boolean {
  if (!isCircuitEnabled(functionName)) return false;
  const state = getCircuitState(functionName);
  if (!state.openedAt) return false;
  const now = Date.now();
  if (now - state.openedAt < CIRCUIT_COOLDOWN_MS) {
    return true;
  }
  state.openedAt = undefined;
  state.failures = 0;
  state.lastFailureAt = undefined;
  return false;
}

function recordFailure(functionName: string, errorType: ErrorType): void {
  if (!isCircuitEnabled(functionName)) return;
  if (!["network", "timeout", "server"].includes(errorType)) return;
  const state = getCircuitState(functionName);
  const now = Date.now();

  if (state.lastFailureAt && now - state.lastFailureAt > CIRCUIT_WINDOW_MS) {
    state.failures = 0;
  }

  state.failures += 1;
  state.lastFailureAt = now;

  if (state.failures >= CIRCUIT_FAILURE_THRESHOLD) {
    state.openedAt = now;
  }
}

function recordSuccess(functionName: string): void {
  if (!isCircuitEnabled(functionName)) return;
  const state = getCircuitState(functionName);
  state.failures = 0;
  state.lastFailureAt = undefined;
  state.openedAt = undefined;
}

function hashString(value: string): string {
  let hash = 5381;
  for (let i = 0; i < value.length; i++) {
    hash = ((hash << 5) + hash) + value.charCodeAt(i);
    hash &= hash;
  }
  return Math.abs(hash).toString(36);
}

function getFallbackKey(functionName: string, body: unknown, userId: string): string {
  if (!FALLBACK_FUNCTIONS.has(functionName)) return `${functionName}:${userId}`;
  let bodyString = "";
  try {
    bodyString = body ? JSON.stringify(body) : "";
  } catch {
    bodyString = "";
  }
  return `${functionName}:${userId}:${hashString(bodyString)}`;
}

function getFallbackData(functionName: string, fallbackKey: string): unknown | null {
  if (!FALLBACK_FUNCTIONS.has(functionName)) return null;
  const cached = fallbackCache.get(fallbackKey);
  if (!cached) return null;
  const now = Date.now();
  if (now - cached.storedAt > FALLBACK_CACHE_TTL_MS) {
    fallbackCache.delete(fallbackKey);
    return null;
  }
  return cached.data;
}

function storeFallbackData(functionName: string, fallbackKey: string, data: unknown): void {
  if (!FALLBACK_FUNCTIONS.has(functionName)) return;
  if (NO_FALLBACK_CACHE_FUNCTIONS.has(functionName)) return;
  fallbackCache.set(fallbackKey, { data, storedAt: Date.now() });
}

function emitDegradedMode(payload: { functionName: string; reason: string; correlationId?: string }) {
  if (typeof window === "undefined") return;
  const now = Date.now();
  const last = degradedEvents.get(payload.functionName);
  if (last && now - last < 15_000) return;
  degradedEvents.set(payload.functionName, now);

  const eventPayload = { ...payload, timestamp: now };
  try {
    sessionStorage.setItem("findigo_degraded_state", JSON.stringify(eventPayload));
  } catch {
    // Ignore storage failures
  }

  try {
    window.dispatchEvent(new CustomEvent("findigo:degraded", { detail: eventPayload }));
  } catch {
    // Ignore dispatch failures
  }
}

async function invokeDirectFetch<T>({
  functionName,
  body,
  authToken,
  timeout,
  correlationId,
}: {
  functionName: string;
  body: unknown;
  authToken: string;
  timeout: number;
  correlationId: string;
}): Promise<EdgeFunctionResponse<T> | null> {
  const config = getSupabasePublicConfig();
  if (!config) {
    return null;
  }

  const url = new URL(`${config.url}/functions/v1/${functionName}`);
  url.searchParams.set("apikey", config.key);

  const payload = typeof body === "object" && body !== null
    ? { ...body, authToken }
    : { payload: body, authToken };

  const controller = new AbortController();
  const timeoutId = timeout > 0 ? setTimeout(() => controller.abort(), timeout) : undefined;

  // Send apikey in both URL and header (some gateways strip query params).
  try {
    const response = await fetch(url.toString(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        "X-Client-Intent": "human",
        apikey: config.key,
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    const responseData = (await response.json().catch(() => null)) as EdgeResponseShape<T> | null;

    if (!response.ok) {
      const errorType = classifyError(
        new Error(responseData?.error?.message || response.statusText || "Request failed"),
        response.status,
      );

      return {
        ok: false,
        error: {
          code: errorType.toUpperCase(),
          message: getUserFriendlyMessage(errorType, responseData?.error?.message),
          correlationId: responseData?.correlationId || correlationId,
          retryable: false,
        },
        correlationId: responseData?.correlationId || correlationId,
      };
    }

    if (responseData?.success === true) {
      return { ok: true, data: responseData.data as T, correlationId: responseData.correlationId || correlationId };
    }

    if (responseData?.ok === true) {
      // Backend returns { ok, data: { sections, jobs, bootstrap } }; unwrap so Dashboard gets card data.
      let inner = (responseData as { data?: unknown }).data;
      if (functionName === "dashboard-initial" && inner != null && typeof inner === "object") {
        const obj = inner as { data?: unknown; sections?: unknown[]; jobs?: unknown[]; bootstrap?: unknown };
        if (obj.data != null && typeof obj.data === "object" && ((obj.data as { sections?: unknown[] }).sections != null || (obj.data as { jobs?: unknown[] }).jobs != null || (obj.data as { bootstrap?: unknown }).bootstrap != null)) {
          inner = obj.data;
        }
      }
      return { ok: true, data: inner as T, correlationId: responseData.correlationId || correlationId };
    }

    if (responseData?.ok === false) {
      const errPayload = {
        ok: false as const,
        error: {
          code: responseData.error?.code || "UNKNOWN",
          message: responseData.error?.message || "Ett fel uppstod",
          correlationId: responseData.error?.correlationId || responseData.correlationId || correlationId,
          retryable: responseData.error?.retryable ?? false,
        },
        correlationId: responseData.correlationId || correlationId,
      };
      // dashboard-initial can return 200 + ok:false + data.bootstrap; pass data so Dashboard can fallback.
      if (functionName === "dashboard-initial" && (responseData as { data?: unknown }).data != null) {
        return { ...errPayload, data: (responseData as { data: T }).data };
      }
      // matches-initial can return 200 + ok:false + data.matches/bootstrap; pass data so Matches can render.
      if (functionName === "matches-initial" && (responseData as { data?: unknown }).data != null) {
        return { ...errPayload, data: (responseData as { data: T }).data };
      }
      return errPayload;
    }

    // Handle raw payload or wrapped shapes; normalize so Dashboard never sees 0 jobs from double-wrap.
    if (functionName === "dashboard-initial" && responseData != null && typeof responseData === "object") {
      const outer = responseData as { data?: unknown; result?: unknown; sections?: unknown[]; jobs?: unknown[]; bootstrap?: unknown };
      const inner = outer.data as { sections?: unknown[]; jobs?: unknown[]; bootstrap?: unknown } | undefined;
      const byResult = outer.result as { sections?: unknown[]; jobs?: unknown[]; bootstrap?: unknown } | undefined;
      const hasPayload = (o: { sections?: unknown[]; jobs?: unknown[]; bootstrap?: unknown }) =>
        (Array.isArray(o.sections) && o.sections.length > 0) || (Array.isArray(o.jobs) && o.jobs.length > 0) || o.bootstrap != null;
      if (inner != null && typeof inner === "object" && hasPayload(inner)) {
        return { ok: true, data: inner as T, correlationId };
      }
      if (byResult != null && typeof byResult === "object" && hasPayload(byResult)) {
        return { ok: true, data: byResult as T, correlationId };
      }
      if (hasPayload(outer)) {
        return { ok: true, data: responseData as T, correlationId };
      }
    }
    // matches-initial: 200 + body with matches/sections/bootstrap → treat as success so UI can render.
    if (functionName === "matches-initial" && responseData != null && typeof responseData === "object") {
      const inner = responseData as { matches?: unknown[]; sections?: unknown[]; bootstrap?: unknown };
      if ((Array.isArray(inner.matches) && inner.matches.length > 0) || (Array.isArray(inner.sections) && inner.sections.length > 0) || inner.bootstrap != null) {
        return { ok: true, data: responseData as T, correlationId };
      }
    }

    return { ok: true, data: responseData as T, correlationId };
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

/**
 * Classify error type from various sources
 */
function classifyError(error: unknown, statusCode?: number): ErrorType {
  if (statusCode === 401 || statusCode === 403) {
    return "auth";
  }
  // 429 Too Many Requests – do not retry; show friendly message (IP rate limit on search-jobs-backend).
  if (statusCode === 429) {
    return "rate_limit";
  }
  // 408 Request Timeout → treat as timeout so we retry and show "Begäran tog för lång tid"
  if (statusCode === 408) {
    return "timeout";
  }
  if (statusCode && statusCode >= 500) {
    return "server";
  }
  if (statusCode && statusCode >= 400 && statusCode < 500) {
    return "client";
  }
  
  const errorMessage = error instanceof Error ? error.message : String(error);
  const lowerMessage = errorMessage.toLowerCase();
  
  if (lowerMessage.includes("timeout") || lowerMessage.includes("timed out")) {
    return "timeout";
  }
  
  if (
    lowerMessage.includes("network") ||
    lowerMessage.includes("fetch") ||
    lowerMessage.includes("cors") ||
    lowerMessage.includes("connection") ||
    lowerMessage.includes("failed to fetch") ||
    lowerMessage.includes("failed to send") ||
    lowerMessage.includes("edge function")
  ) {
    return "network";
  }
  
  return "unknown";
}

/**
 * Get user-friendly error message based on error type
 */
function getUserFriendlyMessage(type: ErrorType, originalMessage?: string): string {
  switch (type) {
    case "network":
    case "timeout":
      return "Kan inte nå servern. Kontrollera din internetanslutning och försök igen.";
    case "auth":
      return "Du behöver logga in igen. Försök igen eller logga ut och in.";
    case "server":
      return "Tillfälligt problem på servern. Försök igen om en stund.";
    case "client":
      return originalMessage || "Ogiltig begäran. Kontrollera dina filter och försök igen.";
    case "rate_limit":
      return "För många anrop. Vänta en minut och ladda om sidan.";
    default:
      return originalMessage || "Ett oväntat fel inträffade. Försök igen.";
  }
}

/**
 * Check if error is retryable
 */
/** DEV: Bygger debug-strang med status, code och raw meddelande. */
function buildDebug(status?: number | null, code?: string, raw?: string): string | undefined {
  if (!import.meta.env.DEV) return undefined;
  const parts = [`status=${status ?? "?"}`, `code=${code ?? "?"}`, raw].filter(Boolean);
  return parts.join(" | ");
}

function isRetryableError(type: ErrorType, statusCode?: number): boolean {
  if (type === "auth") return false;
  if (type === "rate_limit") return false; // 429 – do not retry, user must wait
  // 408 is already classified as "timeout" above; client 4xx (except 408) not retryable
  if (type === "client") return false;
  return type === "network" || type === "timeout" || type === "server";
}

/**
 * Generate correlation ID for request tracking
 */
function generateCorrelationId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Invoke Edge Function with timeout, retry, and robust error handling
 * 
 * @param functionName - Name of the edge function
 * @param options - Request options (body must be JSON-serializable object)
 * @returns Standardized response with ok/data/error structure
 */
export async function invokeEdgeFunction<T = unknown>(
  functionName: string,
  options: EdgeInvokeOptions = {}
): Promise<EdgeFunctionResponse<T>> {
  const {
    body,
    headers = {},
    timeout = 30000, // P1-6 FIX: 30 seconds (longer than edge function timeout 25s to allow retry)
    maxRetries = 2,
    retryDelay = 1000,
    requestId,
  } = options;
  
  const correlationId = generateCorrelationId();
  const trackingRequestId = requestId ?? generateCorrelationId();
  const trackingStartMs = Date.now();
  let lastError: EdgeFunctionError | null = null;
  
  // Session refresh with max attempts and timeout.
  const MAX_REFRESH_ATTEMPTS = 1;
  const REFRESH_TIMEOUT_MS = 5000; // 5 seconds max for refresh
  let refreshAttempts = 0;
  
  // Helper function to safely redirect to login (prevents multiple redirects)
  const redirectToLogin = (reason: string) => {
    if (typeof window !== 'undefined' && !window.location.pathname.includes('/auth')) {
      // Check if we're already redirecting (prevent multiple redirects)
      if (window.__FINDIGO_REDIRECTING_TO_LOGIN) {
        return;
      }
      window.__FINDIGO_REDIRECTING_TO_LOGIN = true;
      
      logWarning(`[edgeFunctionClient] ${reason}, redirecting to login [${correlationId}]`);
      // Use setTimeout to avoid blocking current execution
      setTimeout(() => {
        if (typeof window === 'undefined') return;
        // JSDOM/Vitest throws "Not implemented: navigation" on location.href setter; skip in test
        if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') return;
        try {
          window.location.href = '/auth?session_expired=1';
        } catch {
          // Guard: some envs (e.g. JSDOM) do not implement navigation
        }
      }, 100);
    }
  };
  
  // Ensure we have auth token (defensive: data can be undefined in edge cases)
  // Timeout getSession to avoid hanging when Supabase SDK is slow.
  const GET_SESSION_TIMEOUT_MS = 10000; // 10 seconds max
  let session: { access_token: string; refresh_token?: string; expires_at?: number; user?: { id: string; email?: string } } | null = null;
  try {
    const getSessionPromise = supabase.auth.getSession();
    const getSessionTimeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('getSession timeout')), GET_SESSION_TIMEOUT_MS);
    });
    const sessionResult = await Promise.race([getSessionPromise, getSessionTimeoutPromise]) as { data: { session: typeof session } };
    session = sessionResult?.data?.session;
  } catch (error) {
    logWarning(`[edgeFunctionClient] getSession timeout or error [${correlationId}]:`, error);
    // Continue without session - will trigger refresh attempt below
    session = null;
  }
  if (!session?.access_token) {
    // Try to refresh session with max attempts and timeout.
    if (refreshAttempts < MAX_REFRESH_ATTEMPTS) {
      refreshAttempts++;
      
      try {
        // Timeout on refresh to prevent hanging.
        const refreshPromise = supabase.auth.refreshSession();
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Session refresh timeout')), REFRESH_TIMEOUT_MS);
        });
        
        const raced = await Promise.race([
          refreshPromise,
          timeoutPromise,
        ]);
        const { data: { session: refreshedSession }, error: refreshError } = raced as {
          data: { session: { access_token?: string } | null };
          error: { message?: string } | null;
        };
        
        if (refreshError || !refreshedSession?.access_token) {
          // Redirect to login instead of showing error (prevents infinite loop).
          redirectToLogin('Session refresh failed');
          
          return {
            ok: false,
            error: {
              code: "AUTH_REQUIRED",
              message: "Du behöver logga in igen. Omdirigerar till inloggning...",
              correlationId,
              retryable: false,
            },
            correlationId,
          };
        }
        
        session = refreshedSession as typeof session;
      } catch (error) {
        // Timeout or other error during refresh
        redirectToLogin('Session refresh timeout or error');
        
        return {
          ok: false,
          error: {
            code: "AUTH_REQUIRED",
            message: "Du behöver logga in igen. Omdirigerar till inloggning...",
            correlationId,
            retryable: false,
          },
          correlationId,
        };
      }
    } else {
      // Max refresh attempts reached
      redirectToLogin('Max session refresh attempts reached');
      
      return {
        ok: false,
        error: {
          code: "AUTH_REQUIRED",
          message: "Du behöver logga in igen. Omdirigerar till inloggning...",
          correlationId,
          retryable: false,
        },
        correlationId,
      };
    }
  }
  
  // Use the refreshed session token.
  // Double-check that we have a valid token after refresh
  const authToken = session?.access_token || "";
  if (!authToken) {
    // If we still don't have a token after refresh, redirect.
    redirectToLogin('No auth token available after refresh attempt');
    
    return {
      ok: false,
      error: {
        code: "AUTH_REQUIRED",
        message: "Du behöver logga in igen. Omdirigerar till inloggning...",
        correlationId,
        retryable: false,
      },
      correlationId,
    };
  }
  
  const userId = session?.user?.id || "anon";
  const fallbackKey = getFallbackKey(functionName, body, userId);
  const buildSuccess = (data: T, correlationOverride?: string): EdgeFunctionResponse<T> => {
    recordSuccess(functionName);
    storeFallbackData(functionName, fallbackKey, data);
    logRequest({
      requestId: trackingRequestId,
      route: typeof window !== "undefined" ? window.location.pathname : "",
      endpoint: functionName,
      method: "POST",
      status: 200,
      durationMs: Date.now() - trackingStartMs,
      errorName: null,
      errorMessage: null,
      timestamp: Date.now(),
    });
    return {
      ok: true,
      data,
      correlationId: correlationOverride ?? correlationId,
    };
  };
  const buildCachedFallback = (data: T, reason: string): EdgeFunctionResponse<T> & { cached: true } => {
    emitDegradedMode({ functionName, reason, correlationId });
    return {
      ok: true,
      data,
      correlationId,
      cached: true,
    };
  };

  if (isCircuitOpen(functionName)) {
    if (!NO_FALLBACK_CACHE_FUNCTIONS.has(functionName)) {
      const cached = getFallbackData(functionName, fallbackKey);
      if (cached) {
        return buildCachedFallback(cached as T, "circuit_open_cache");
      }
    }
    if (!NO_FALLBACK_CACHE_FUNCTIONS.has(functionName)) {
      emitDegradedMode({ functionName, reason: "circuit_open", correlationId });
    }
    return {
      ok: false,
      error: {
        code: "CIRCUIT_OPEN",
        message: "Tjänsten är tillfälligt överbelastad. Försök igen om en stund.",
        correlationId,
        retryable: true,
      },
      correlationId,
    };
  }

  const authHeaders = {
    Authorization: `Bearer ${authToken}`,
    "X-Client-Intent": "human",
    "X-Correlation-Id": correlationId,
    ...(requestId ? { "X-Request-Id": requestId } : {}),
    ...headers,
  };

  // Retry loop
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Timeout via Promise.race (Supabase client doesn't support AbortSignal). Skip race when timeout <= 0 (e.g. unit tests).
      const invokePromise = supabase.functions.invoke(functionName, {
        body,
        headers: authHeaders,
      });

      let response;
      try {
        response = timeout > 0
          ? await Promise.race([
              invokePromise,
              new Promise<never>((_, reject) => {
                setTimeout(() => reject(new Error("timeout")), timeout);
              }),
            ])
          : await invokePromise;
      } catch (invokeError) {
        // Supabase SDK can throw for 404; catch and handle.
        // Especially important for healthcheck which is optional
        // FAANG-LEVEL: Prevent healthcheck 404 from causing any UI issues
        const errorMessage = invokeError instanceof Error ? invokeError.message : String(invokeError);
        const errorObj = invokeError as Record<string, unknown> & {
          status?: number; code?: string; error_type?: string;
          response?: { status?: number; code?: string };
          filename?: string;
        };
        const errorString = String(invokeError);
        if (functionName === "healthcheck") {
          if (attempt < maxRetries) {
            const delay = retryDelay * Math.pow(2, attempt);
            logWarning(`[edgeFunctionClient] Timeout error, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1}) [${correlationId}]`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue; // Retry
          }
          
          // Not retryable - return timeout error
          return {
            ok: false,
            error: {
              code: "TIMEOUT",
              message: getUserFriendlyMessage("timeout", errorMessage),
              correlationId,
              retryable: false,
              debug: buildDebug(undefined, "TIMEOUT", errorMessage),
            },
            correlationId,
          };
        }
        
        // STABILITY FIX: Better error handling for Supabase SDK exceptions
        // "Failed to send a request to the Edge Function" usually means network/CORS
        // NOTE: timeout errors are handled above, so exclude them here
        const isNetworkError = 
          errorMessage.includes("Failed to send") ||
          errorMessage.includes("fetch") ||
          errorMessage.includes("network") ||
          errorMessage.includes("CORS") ||
          errorMessage.includes("connection") ||
          errorString.includes("Failed to send");
        
        // Check all possible 404 indicators
        const is404 = 
          errorMessage.includes('404') || 
          errorMessage.includes('NOT_FOUND') ||
          errorMessage.includes('not found') ||
          errorMessage.includes('Requested function was not found') ||
          errorMessage.includes('Edge function returned 404') ||
          errorMessage.includes('Edge Function returned 404') ||
          errorString.includes('404') ||
          errorString.includes('NOT_FOUND') ||
          errorObj?.status === 404 ||
          errorObj?.code === "NOT_FOUND" ||
          errorObj?.error_type === "RUNTIME_ERROR" ||
          errorObj?.response?.status === 404 ||
          errorObj?.response?.code === "NOT_FOUND" ||
          // Check for structured error format
          (errorObj?.filename && 
           (errorObj?.error_type === 'RUNTIME_ERROR' || errorObj?.code === 'NOT_FOUND'));
        
        // Healthcheck 404 - return success instead of error
        if (functionName === "healthcheck" && is404) {
          logWarning("[edgeFunctionClient] Healthcheck 404 - returning success (function is optional)");
          return {
            ok: true,
            data: {
              status: "healthy",
              checks: [
                {
                  function: "search-jobs-backend",
                  status: "healthy",
                },
                {
                  function: "calculate-matches",
                  status: "healthy",
                },
              ],
              message: "Healthcheck function not deployed - assuming healthy",
            } as T,
            correlationId,
          };
        }
        
        // If it's a network error, classify it and let retry logic handle it
        if (isNetworkError) {
          const networkError: EdgeFunctionError = {
            type: "network",
            message: errorMessage,
            correlationId,
            retryable: attempt < maxRetries,
            originalError: invokeError,
          };
          lastError = networkError;

          // On first attempt, try direct fetch for critical functions (CORS/network fallback)
          if (attempt === 0 && DIRECT_FETCH_FUNCTIONS.has(functionName)) {
            const directResponse = await invokeDirectFetch<T>({
              functionName,
              body,
              authToken,
              timeout: timeout > 0 ? timeout : 30000,
              correlationId,
            });
            if (directResponse?.ok) return buildSuccess(directResponse.data as T, directResponse.correlationId);
            if (directResponse) return directResponse;
          }
          
          if (networkError.retryable) {
            const delay = retryDelay * Math.pow(2, attempt);
            logWarning(`[edgeFunctionClient] Network error, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1}) [${correlationId}]`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue; // Retry
          }
          
          // Not retryable - return error
          return {
            ok: false,
            error: {
              code: "NETWORK",
              message: getUserFriendlyMessage("network", errorMessage),
              correlationId,
              retryable: false,
              debug: buildDebug(undefined, "NETWORK", errorMessage),
            },
            correlationId,
          };
        }
        
        // 404 / function not found: return tydligt fel istället för throw (gör felet åtgärdbart)
        if (is404) {
          const msg = import.meta.env.DEV
            ? `Edge-funktionen "${functionName}" hittades inte. Kör "supabase functions serve" lokalt eller deploya funktionen.`
            : "Tjänsten är tillfälligt otillgänglig. Försök igen.";
          return {
            ok: false,
            error: {
              code: "FUNCTION_NOT_FOUND",
              message: msg,
              correlationId,
              retryable: true,
              debug: buildDebug(errorObj?.status ?? 404, "NOT_FOUND", errorMessage),
            },
            correlationId,
          };
        }
        
        // For other unhandled invoke errors, re-throw to be handled by outer catch
        throw invokeError;
      }
      
      // Handle Supabase SDK response format
      if (response.error) {
        // Healthcheck 404 is expected when function not deployed; treat as success.
        // Healthcheck is optional - app should work fine without it
        if ((response.error.status === 404 || response.error.code === "NOT_FOUND") && functionName === "healthcheck") {
          return buildSuccess({
            status: "healthy",
            checks: [
              {
                function: "search-jobs-backend",
                status: "healthy",
              },
              {
                function: "calculate-matches",
                status: "healthy",
              },
            ],
            message: "Healthcheck function not deployed - assuming healthy",
          } as T);
        }
        
        // Prefer error message from response.data when present.
        // Supabase SDK sets response.error with generic message, but actual error is in response.data
        let actualErrorMessage = response.error.message || "Ett fel uppstod";
        let actualErrorCode = response.error.code || "UNKNOWN";
        let actualRetryable = false;
        
        try {
          // Try to parse response.data as JSON to get actual error from backend
          if (response.data) {
            const responseData = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
            if (responseData?.ok === false && responseData?.error) {
              // Backend returned structured error - use it
              actualErrorMessage = responseData.error.message || actualErrorMessage;
              actualErrorCode = responseData.error.code || actualErrorCode;
              actualRetryable = responseData.error.retryable ?? false;
            } else if (responseData?.error?.message) {
              // Legacy format
              actualErrorMessage = responseData.error.message;
            }
          }
        } catch {
          // If parsing fails, use response.error.message
        }
        
        // Backend auth errors (Invalid JWT, 401) – treat as auth so we redirect to login
        const status401 = response.error?.status === 401;
        const isAuthMessage =
          status401 ||
          (typeof actualErrorMessage === "string" &&
            (actualErrorMessage.includes("Invalid JWT") ||
              actualErrorMessage.includes("Invalid or expired token") ||
              actualErrorMessage.includes("Missing or invalid Authorization header") ||
              actualErrorMessage.includes("Missing token") ||
              actualErrorMessage.includes("Unauthorized")));
        const errorType = isAuthMessage
          ? "auth"
          : classifyError(response.error, response.error.status);
        const retryable = isRetryableError(errorType, response.error.status) || actualRetryable;

        if (errorType === "auth" || status401) {
          redirectToLogin(`Edge function ${functionName} returned 401`);
        }
        
        // P2-9 FIX: Check for CORS errors specifically
        const isCorsError = response.error.status === 403 && 
          (actualErrorMessage.includes('CORS') || 
           actualErrorMessage.includes('cors') ||
           actualErrorMessage.includes('Origin not allowed'));
        
        lastError = {
          type: errorType,
          message: actualErrorMessage,
          correlationId,
          retryable,
          originalError: response.error,
        };
        
        // If retryable and we haven't exceeded max retries, retry
        if (retryable && attempt < maxRetries) {
          const delay = retryDelay * Math.pow(2, attempt); // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        
        // Not retryable or exceeded retries
        // OBSERVABILITY: Send non-2xx errors to Sentry (fire and forget)
        // Do not send healthcheck 404 to Sentry (expected when function not deployed).
        // FAANG-LEVEL: Also check for healthcheck in error message/code to catch all cases
        const isHealthcheckError = 
          functionName === "healthcheck" ||
          response.error.message?.includes('healthcheck') ||
          response.error.code === 'NOT_FOUND' && response.error.message?.includes('healthcheck');
        
        if (import.meta.env.PROD && response.error.status && response.error.status >= 400 && !isHealthcheckError) {
          const errToReport = new Error(`Edge function error: ${response.error.message}`);
          const sentryContext = {
            correlationId,
            tags: {
              functionName,
              errorType,
              statusCode: String(response.error.status),
              isCorsError: String(isCorsError),
            },
            extra: { retryable, attempt },
          };
          import('./sentry')
            .then(({ captureException }) => {
              captureException(errToReport, sentryContext);
            })
            .catch((sentryErr) => {
              logError("[edgeFunctionClient] Sentry unavailable; error logged locally:", errToReport);
              logWarning("[edgeFunctionClient] Sentry captureException failed:", sentryErr);
            });
        }
        
        // P2-9 FIX: Return specific CORS error message
        // Use backend error message when available.
        const errorMessage = isCorsError
          ? "CORS-fel: Din origin är inte tillåten. Kontakta support om problemet kvarstår."
          : getUserFriendlyMessage(errorType, actualErrorMessage);

        recordFailure(functionName, errorType);
        const fallback = getFallbackData(functionName, fallbackKey);
        if (fallback) {
          return buildCachedFallback(fallback as T, "fallback_cache");
        }

        // When SDK returns error (e.g. 502, CORS), try direct fetch once so E2E/production can still load.
        if (attempt === 0 && DIRECT_FETCH_FUNCTIONS.has(functionName) && (functionName === "dashboard-initial" || functionName === "matches-initial" || functionName === "search-jobs-backend" || functionName === "applications-initial")) {
          const directResponse = await invokeDirectFetch<T>({
            functionName,
            body,
            authToken,
            timeout,
            correlationId,
          });
          if (directResponse?.ok) {
            return buildSuccess(directResponse.data as T, directResponse.correlationId);
          }
          if (directResponse && !directResponse.ok && (directResponse as { data?: unknown }).data != null) {
            return { ...directResponse, data: (directResponse as { data: T }).data };
          }
        }

        return {
          ok: false,
          error: {
            code: isCorsError
              ? "CORS_REJECTED"
              : errorType === "auth"
                ? "AUTH_REQUIRED"
                : actualErrorCode !== "UNKNOWN"
                  ? actualErrorCode
                  : errorType.toUpperCase(),
            message: errorMessage,
            correlationId,
            retryable: isCorsError ? false : retryable, // CORS errors are never retryable
            debug: buildDebug(response.error?.status, actualErrorCode, actualErrorMessage),
          },
          correlationId,
        };
      }
      
      // Check if response.data has the expected format
      // Backend should return { success: true, data: ... } or { ok: true, data: ... }
      const responseData = response.data as any;

      // Handle legacy format: { success: true, data: ... }
      if (responseData?.success === true) {
        return buildSuccess(responseData.data as T);
      }
      
      // Handle new format: { ok: true, data: ..., debugVersion?: ... }
      if (responseData?.ok === true) {
        // SDK or proxy may put payload in .data or double-wrap; use innermost with bootstrap/sections/jobs.
        let innerData = (responseData as { data?: T }).data;
        if (functionName === "dashboard-initial" && innerData != null && typeof innerData === "object" && "data" in innerData) {
          const nested = (innerData as { data?: T }).data;
          if (nested != null && typeof nested === "object" && ("bootstrap" in nested || "jobs" in nested || "sections" in nested)) {
            innerData = nested;
          }
        }
        if (functionName === "dashboard-initial" && (innerData == null || (typeof innerData === "object" && !("bootstrap" in innerData) && !("jobs" in innerData) && !("sections" in innerData)))) {
          const asPayload = responseData as { bootstrap?: unknown; jobs?: unknown; sections?: unknown };
          if (asPayload && (asPayload.bootstrap != null || (Array.isArray(asPayload.jobs) && asPayload.jobs.length > 0) || (Array.isArray(asPayload.sections) && asPayload.sections.length > 0))) {
            innerData = responseData as T;
          }
        }
        const result: EdgeFunctionResponse<T> = buildSuccess(
          (innerData ?? responseData.data) as T,
          responseData.correlationId || correlationId
        );
        if (responseData.debugVersion && import.meta.env.DEV) {
          (result as EdgeFunctionResponse<T> & { debugVersion?: string }).debugVersion = responseData.debugVersion;
        }
        return result;
      }
      
      // Handle error format: { ok: false, error: { message } | string, requestId?, data? }
      // When dashboard-initial returns 200 + ok:false + data.bootstrap, include data so Dashboard can fallback.
      if (responseData?.ok === false) {
        const code = responseData.error?.code || "UNKNOWN";
        const msg = responseData.error?.message || "Ett fel uppstod";
        const payloadData = (responseData as { data?: T }).data;
        const errorResult: EdgeFunctionResponse<T> = {
          ok: false,
          error: {
            code,
            message: msg,
            correlationId: responseData.error?.correlationId || correlationId,
            retryable: responseData.error?.retryable ?? false,
            debug: buildDebug(undefined, code, msg),
          },
          correlationId: (responseData as { requestId?: string }).requestId ?? responseData.correlationId ?? correlationId,
        };
        // For dashboard-initial, include data so Dashboard fallback can extract jobs/bootstrap
        if (functionName === "dashboard-initial" && payloadData != null) {
          return { ...errorResult, data: payloadData };
        }
        return errorResult;
      }

      // Check for error field only when backend did NOT set ok/success. If ok: true, we already returned data above.
      // Some edge functions return status 200 with { error: "..." } without ok/success; treat as error.
      if (responseData?.error) {
        const errorMessage = typeof responseData.error === 'string'
          ? responseData.error
          : responseData.error?.message || "Ett fel uppstod";
        const errorCode = typeof responseData.error === 'object' && responseData.error?.code
          ? responseData.error.code
          : "UNKNOWN";
        const retryable = typeof responseData.error === 'object' && responseData.error?.retryable !== undefined
          ? responseData.error.retryable
          : false;

        return {
          ok: false,
          error: {
            code: errorCode,
            message: errorMessage,
            correlationId: responseData.error?.correlationId || correlationId,
            retryable,
            debug: buildDebug(undefined, errorCode, errorMessage),
          },
          correlationId: responseData.correlationId || correlationId,
        };
      }

      // If envelope has .data, return inner so Dashboard never gets 0 jobs from shape.
      const inner = (responseData as { data?: T }).data;
      if (functionName === "dashboard-initial" && inner != null && typeof inner === "object" && "bootstrap" in inner) {
        return buildSuccess(inner);
      }
      if (functionName === "matches-initial" && inner != null && typeof inner === "object" && ("matches" in inner || "bootstrap" in inner)) {
        return buildSuccess(inner);
      }
      return buildSuccess(responseData as T);
      
    } catch (error) {
      // Handle timeout (AbortError)
      if (error instanceof Error && (error.name === "AbortError" || error.message.includes("timeout"))) {
        const isRequestTimeout = true; // we're in the timeout branch
        const isSessionTimeout = error.message.includes("Session refresh timeout");
        // Only handle as request timeout if it's not a session refresh timeout
        if (isRequestTimeout && !isSessionTimeout) {
          const errorType: ErrorType = "timeout";
          const retryable = attempt < maxRetries;
          
          lastError = {
            type: errorType,
            message: "Begäran tog för lång tid",
            correlationId,
            retryable,
            originalError: error,
          };
          
          if (retryable) {
            const delay = retryDelay * Math.pow(2, attempt);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
          
          // Not retryable - return timeout error
          return {
            ok: false,
            error: {
              code: "TIMEOUT",
              message: getUserFriendlyMessage(errorType, lastError.message),
              correlationId,
              retryable: false,
              debug: buildDebug(undefined, "TIMEOUT", lastError.message),
            },
            correlationId,
          };
        }
      }
      
      // Handle network errors
      const errorType = classifyError(error);
      const retryable = isRetryableError(errorType) && attempt < maxRetries;
      
      lastError = {
        type: errorType,
        message: error instanceof Error ? error.message : String(error),
        correlationId,
        retryable,
        originalError: error,
      };

      // FAANG-level: For search-jobs-backend, try direct fetch on first failure (CORS or timeout/network)
      if (
        attempt === 0 &&
        DIRECT_FETCH_FUNCTIONS.has(functionName) &&
        (isCorsLikelyError(error) || lastError?.type === "timeout" || lastError?.type === "network")
      ) {
        const directResponse = await invokeDirectFetch<T>({
          functionName,
          body,
          authToken,
          timeout,
          correlationId,
        });

        if (directResponse?.ok) {
          return buildSuccess(directResponse.data as T, directResponse.correlationId);
        }

        if (directResponse) {
          return directResponse;
        }
      }
      
      if (retryable) {
        const delay = retryDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      // Not retryable or exceeded retries
      recordFailure(functionName, errorType);
      if (!NO_FALLBACK_CACHE_FUNCTIONS.has(functionName)) {
        const fallback = getFallbackData(functionName, fallbackKey);
        if (fallback) {
          return buildCachedFallback(fallback as T, "fallback_cache");
        }
      }

      return {
        ok: false,
        error: {
          code: errorType.toUpperCase(),
          message: getUserFriendlyMessage(errorType, lastError.message),
          correlationId,
          retryable: false,
          debug: buildDebug(undefined, errorType.toUpperCase(), lastError.message),
        },
        correlationId,
      };
    }
  }
  
  // Should never reach here, but TypeScript needs this
  logRequest({
    requestId: trackingRequestId,
    route: typeof window !== "undefined" ? window.location.pathname : "",
    endpoint: functionName,
    method: "POST",
    status: null,
    durationMs: Date.now() - trackingStartMs,
    errorName: lastError?.type ?? "UNKNOWN",
    errorMessage: lastError?.message ?? "Ett oväntat fel inträffade",
    timestamp: Date.now(),
  });
  return {
    ok: false,
    error: {
      code: "UNKNOWN",
      message: lastError?.message || "Ett oväntat fel inträffade",
      correlationId,
      retryable: false,
      debug: buildDebug(undefined, "UNKNOWN", lastError?.message),
    },
    correlationId,
  };
}

/**
 * Helper to check if error is retryable
 */
export function isRetryable(response: EdgeFunctionResponse): boolean {
  return response.error?.retryable ?? false;
}

/**
 * Helper to get error type from response
 */
export function getErrorType(response: EdgeFunctionResponse): ErrorType | null {
  if (!response.error) return null;
  
  const code = response.error.code.toLowerCase();
  if (code.includes("network")) return "network";
  if (code.includes("timeout")) return "timeout";
  if (code.includes("auth")) return "auth";
  if (code.includes("server")) return "server";
  if (code.includes("rate_limit")) return "rate_limit";
  if (code.includes("client")) return "client";

  return "unknown";
}

// --- Edge response contracts (search-jobs-backend) ---

/** One job item as returned by search-jobs-backend. Kept minimal for broad compatibility. */
export interface SearchJobsBackendJob {
  id: string;
  title: string;
  company: string | null;
  description?: string | null;
  location?: string | null;
  country_code?: string | null;
  location_status?: string | null;
  url?: string | null;
  category?: string | null;
  tags?: string[] | null;
  [key: string]: unknown;
}

/** Response shape from search-jobs-backend edge function. */
export interface SearchJobsBackendResponse {
  jobs: SearchJobsBackendJob[];
  totalCount: number;
  absoluteCount?: number;
  page?: number;
  pageSize?: number;
  hasMore?: boolean;
  cached?: boolean;
  suggestions?: string[];
  message?: string;
  debugVersion?: string;
}

/** Minimal job card from dashboard-initial (no description). */
export interface DashboardInitialJob {
  id: string;
  title: string;
  company: string;
  location: string | null;
  country_code?: string | null;
  location_status?: string | null;
  postedAt: string | null;
  url: string | null;
  work_mode?: string | null;
  job_type?: string | null;
  source?: string | null;
  apply_url?: string | null;
  apply_method?: string | null;
  saved: boolean;
  applied: boolean;
}

/** CV status from bootstrap (single source of truth; avoids blink in snabbansökan). */
export interface BootstrapCvStatus {
  hasCv: boolean;
  source: "bootstrap";
}

/** One section in dashboard-initial response (Pro: top_matches, top_jobs, job_list). */
export interface DashboardInitialSection {
  type: 'top_matches' | 'top_jobs' | 'job_list';
  items: unknown[];
  page?: { nextCursor: string | null; totalCount: number };
}

/**
 * Canonical job list from dashboard-initial: prefer sections (job_list, then top_jobs, then top_matches), fallback to legacy jobs.
 * Ensures if backend sends items in sections, UI gets them (no shape drift / silent empty).
 * top_matches items have structure { job: {...}, score, reasons } - unwrap .job from each match.
 */
export function getDashboardJobsFromPayload(data: {
  sections?: DashboardInitialSection[];
  jobs?: DashboardInitialJob[];
}): DashboardInitialJob[] {
  const sections = Array.isArray(data.sections) ? data.sections : [];
  if (import.meta.env.DEV) {
    const types = sections.map((s) => (s && typeof s === "object" && "type" in s ? (s as { type?: string }).type : undefined));
    console.log("[getDashboardJobsFromPayload] sections:", sections.length, "types:", types);
  }
  const jobListSection = sections.find((s) => s && typeof s === "object" && (s as { type?: string }).type === "job_list");
  if (jobListSection && typeof jobListSection === "object" && Array.isArray((jobListSection as { items?: unknown[] }).items) && (jobListSection as { items: unknown[] }).items.length > 0) {
    const items = (jobListSection as { items: unknown[] }).items;
    if (import.meta.env.DEV) console.log("[getDashboardJobsFromPayload] found job_list:", items.length);
    return items as DashboardInitialJob[];
  }
  const topJobsSection = sections.find((s) => s && typeof s === "object" && (s as { type?: string }).type === "top_jobs");
  if (topJobsSection && typeof topJobsSection === "object" && Array.isArray((topJobsSection as { items?: unknown[] }).items) && (topJobsSection as { items: unknown[] }).items.length > 0) {
    const items = (topJobsSection as { items: unknown[] }).items;
    if (import.meta.env.DEV) console.log("[getDashboardJobsFromPayload] found top_jobs:", items.length);
    return items as DashboardInitialJob[];
  }
  // top_matches items are match objects with nested .job; unwrap to job objects.
  const topMatchesSection = sections.find((s) => s && typeof s === "object" && (s as { type?: string }).type === "top_matches");
  if (topMatchesSection && typeof topMatchesSection === "object" && Array.isArray((topMatchesSection as { items?: unknown[] }).items) && (topMatchesSection as { items: unknown[] }).items.length > 0) {
    const topItems = (topMatchesSection as { items: unknown[] }).items;
    if (import.meta.env.DEV) console.log("[getDashboardJobsFromPayload] found top_matches:", topItems.length, "items");
    const jobs: DashboardInitialJob[] = [];
    for (const item of topItems) {
      if (item && typeof item === "object" && "job" in item && item.job && typeof item.job === "object") {
        jobs.push(item.job as DashboardInitialJob);
      } else if (import.meta.env.DEV) {
        console.log("[getDashboardJobsFromPayload] top_matches item missing .job:", item);
      }
    }
    if (import.meta.env.DEV) console.log("[getDashboardJobsFromPayload] unwrapped jobs from top_matches:", jobs.length);
    if (jobs.length > 0) return jobs;
  }
  if (import.meta.env.DEV) console.log("[getDashboardJobsFromPayload] fallback to data.jobs:", data.jobs?.length ?? 0);
  return data.jobs ?? [];
}

/**
 * Unwrap dashboard-initial response when backend returns nested .data (e.g. { data: { bootstrap, jobs, ... } }).
 * Returns the inner payload when present and valid; otherwise null so caller can use raw.
 */
export function unwrapDashboardInitialData(raw: unknown): DashboardInitialResponse | null {
  if (!raw || typeof raw !== "object") return null;
  const data = raw as Record<string, unknown>;
  if (data.data == null) return null;
  const hasTopLevel =
    data.bootstrap != null ||
    (data.jobs != null && Array.isArray(data.jobs)) ||
    (Array.isArray(data.sections) && data.sections.length > 0);
  if (hasTopLevel) return null; // already top-level shape
  const inner = data.data as Record<string, unknown> | null;
  if (!inner || typeof inner !== "object") return null;
  const hasInner =
    inner.bootstrap != null ||
    inner.jobs != null ||
    (Array.isArray(inner.sections) && inner.sections.length > 0);
  return hasInner ? (inner as unknown as DashboardInitialResponse) : null;
}

/** Canonical extraction of job list from dashboard-initial payload (top-level or nested .data/.result, sections or jobs). Handles double-wrapped data.data. */
export function getDashboardJobsFromPayloadP0(raw: unknown): DashboardInitialJob[] {
  if (!raw || typeof raw !== "object") return [];
  const d = raw as Record<string, unknown>;
  const inner = d.data as Record<string, unknown> | undefined;
  const inner2 = inner?.data as Record<string, unknown> | undefined;
  const byResult = d.result as Record<string, unknown> | undefined;
  const sections = (d.sections ?? inner?.sections ?? inner2?.sections ?? byResult?.sections) as { type?: string; items?: unknown[] }[] | undefined;
  const jobs = (d.jobs ?? inner?.jobs ?? inner2?.jobs ?? byResult?.jobs) as DashboardInitialJob[] | undefined;
  if (Array.isArray(sections)) {
    // Check job_list, top_jobs, then top_matches (unwrap .job from match items).
    for (const type of ["job_list", "top_jobs"]) {
      const sec = sections.find((s) => s.type === type);
      if (sec?.items && Array.isArray(sec.items) && sec.items.length > 0) return sec.items as DashboardInitialJob[];
    }
    // top_matches: unwrap .job from each match item
    const topMatchesSec = sections.find((s) => s.type === "top_matches");
    if (topMatchesSec?.items && Array.isArray(topMatchesSec.items) && topMatchesSec.items.length > 0) {
      const extractedJobs: DashboardInitialJob[] = [];
      for (const item of topMatchesSec.items) {
        if (item && typeof item === "object" && "job" in item && item.job && typeof item.job === "object") {
          extractedJobs.push(item.job as DashboardInitialJob);
        }
      }
      if (extractedJobs.length > 0) return extractedJobs;
    }
  }
  if (Array.isArray(jobs) && jobs.length > 0) return jobs;
  return [];
}

/** Deep-scan payload for any array of job-like objects (id, title, company). Last resort when canonical paths return []. */
export function findJobsDeepInPayload(raw: unknown, maxDepth = 12): DashboardInitialJob[] {
  if (!raw || typeof raw !== "object") return [];
  const seen = new Set<object>();
  const visit = (obj: unknown, depth: number): DashboardInitialJob[] => {
    if (depth <= 0) return [];
    if (obj == null || typeof obj !== "object") return [];
    if (seen.has(obj as object)) return [];
    seen.add(obj as object);
    if (Array.isArray(obj)) {
      // Check if all items are direct job objects (id, title, company)
      const allJobLike = obj.length > 0 && obj.every(
        (item) =>
          item != null &&
          typeof item === "object" &&
          "id" in item &&
          "title" in item &&
          "company" in item &&
          (item as { id?: unknown }).id != null
      );
      if (allJobLike) return obj as DashboardInitialJob[];
      // Check if items are match objects with nested .job (top_matches structure).
      const allMatchLike = obj.length > 0 && obj.every(
        (item) =>
          item != null &&
          typeof item === "object" &&
          "job" in item &&
          item.job != null &&
          typeof item.job === "object" &&
          "id" in item.job &&
          "title" in item.job &&
          "company" in item.job &&
          (item.job as { id?: unknown }).id != null
      );
      if (allMatchLike) {
        return obj.map((item) => (item as { job: DashboardInitialJob }).job);
      }
      for (const item of obj) {
        const found = visit(item, depth - 1);
        if (found.length > 0) return found;
      }
      return [];
    }
    for (const key of Object.keys(obj as Record<string, unknown>)) {
      const found = visit((obj as Record<string, unknown>)[key], depth - 1);
      if (found.length > 0) return found;
    }
    return [];
  };
  return visit(raw, maxDepth);
}

/** Response shape from dashboard-initial edge function (one-call first paint). Pro users get sections (top_matches + top_jobs + job_list) in same payload. */
export interface DashboardInitialResponse {
  bootstrap: {
    profile: {
      id: string;
      locale?: string;
      roles?: string[];
      location?: string | null;
      city?: string | null;
      country?: string | null;
      remote_ok?: boolean;
      work_modes?: string[];
      assignment_types?: string[];
      seniority?: string | null;
      updated_at?: string | null;
    } | null;
    onboarding: { completed: boolean; step: number };
    savedJobIds: string[];
    appliedJobIds: string[];
    cvStatus?: BootstrapCvStatus;
  };
  /** Flattened job list (always present). Pro users also get data.sections with top_matches, top_jobs, job_list. */
  jobs: DashboardInitialJob[];
  totalCount: number;
  nextCursor: string | null;
  hasMore: boolean;
  /** Pro only: Top matches + Top jobs + job list in same payload. Frontend may render sections for Pro. */
  sections?: DashboardInitialSection[];
  user?: { id: string; isPro: boolean; locale?: string };
}

export interface DashboardInitialEdgeResponse {
  ok: boolean;
  data?: DashboardInitialResponse;
  error?: string;
  meta?: { requestId?: string; generatedAt?: string };
}

/** Sub-scores (0..1) for "Varför fick jag detta jobb?" – MATCH-P0-4. */
export interface MatchSubScores {
  roleMatch: number;
  skillsMatch: number;
  locationMatch: number;
  recency: number;
}

/** Minimal match card from matches-initial. */
export interface MatchesInitialMatch {
  id: string;
  title: string;
  company: string;
  location: string | null;
  country_code?: string | null;
  location_status?: string | null;
  url: string | null;
  postedAt: string | null;
  matchScore: number;
  matchReasons: string[];
  matchSubScores?: MatchSubScores | null;
  /** Which hard filters let this job through (Språk, Plats, Roll) */
  passedHardFilters?: string[] | null;
  saved: boolean;
  applied: boolean;
  apply_method?: string | null;
  apply_url?: string | null;
  auto_apply_ready?: boolean | null;
}

export interface MatchesInitialResponse {
  meta?: {
    requestId?: string;
    generatedAt?: string;
    timings?: { totalMs?: number };
    match_run_id?: string;
    stale?: boolean;
    stale_source?: string;
    stale_reason?: string;
    stale_generated_at?: string;
  };
  bootstrap: {
    profile: { id: string; locale?: string; roles?: string[]; location?: string | null; city?: string | null; country?: string | null; remote_ok?: boolean; work_modes?: string[]; assignment_types?: string[]; seniority?: string | null; updated_at?: string | null } | null;
    onboarding: { completed: boolean; step: number };
    savedJobIds: string[];
    appliedJobIds: string[];
    cvStatus?: BootstrapCvStatus;
  };
  /** From matches-initial; use for first-paint isPro so no useSubscription fetch before first render. */
  user?: { id: string; isPro: boolean };
  profileVersion?: string;
  matches: MatchesInitialMatch[];
  totalCount: number;
  nextCursor: string | null;
  hasMore: boolean;
  generated_at?: string;
}

/** Application with latestAttempt from applications-initial. */
export interface ApplicationsInitialItem {
  id: string;
  job_id: string;
  job_title: string;
  company_name: string | null;
  applied_at: string | null;
  status: string;
  destination: string | null;
  created_at?: string;
  latestAttempt: {
    id: string;
    status: string;
    at: string;
    error_reason?: string | null;
    failure_reason?: string | null;
    platform?: string | null;
    status_reason_code?: string | null;
    started_at?: string | null;
  } | null;
}

export interface ApplicationsInitialResponse {
  applications: ApplicationsInitialItem[];
}

/** Saved job card from saved-jobs-initial. */
export interface SavedJobsInitialItem {
  id: string;
  job_id: string;
  job_title: string;
  company_name: string | null;
  saved_at: string;
  job_data: Record<string, unknown>;
}

export interface SavedJobsInitialResponse {
  savedJobs: SavedJobsInitialItem[];
}
